// FILE 27: src/components/contact/InquiryForm.js
import React, { useState } from 'react';
import { useEmailJS } from '../../hooks/useEmailJS';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import './InquiryForm.css';

const PROJECT_TYPES = ['Residential', 'Commercial', 'Other'];

const INITIAL_ERRORS = {
  fullName: '',
  email: '',
  message: '',
};

function validate(fields) {
  const errors = { ...INITIAL_ERRORS };
  let valid = true;

  if (!fields.fullName.trim()) {
    errors.fullName = 'Full name is required.';
    valid = false;
  }

  if (!fields.email.trim()) {
    errors.email = 'Email address is required.';
    valid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    errors.email = 'Please enter a valid email address.';
    valid = false;
  }

  if (!fields.message.trim()) {
    errors.message = 'Message is required.';
    valid = false;
  }

  return { errors, valid };
}

/**
 * @param {string} [initialTileInterest] - pre-populates the "Tile of Interest" field
 */
export default function InquiryForm({ initialTileInterest = '' }) {
  const { status, errorMessage, send, reset } = useEmailJS();

  const [fields, setFields] = useState({
    fullName: '',
    email: '',
    phone: '',
    projectType: 'Residential',
    tileInterest: initialTileInterest,
    message: '',
    honeypot: '',
  });

  const [fieldErrors, setFieldErrors] = useState(INITIAL_ERRORS);

  const isDisabled = status === 'sending';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check
    if (fields.honeypot) return;

    const { errors, valid } = validate(fields);
    if (!valid) {
      setFieldErrors(errors);
      return;
    }

    await send({
      to_email: 'inquiries@ardensurfaces.com',
      from_name: fields.fullName,
      from_email: fields.email,
      phone: fields.phone || 'N/A',
      project_type: fields.projectType,
      tile_interest: fields.tileInterest || 'N/A',
      message: fields.message,
    });
  };

  const handleSendAnother = () => {
    reset();
    setFields({
      fullName: '',
      email: '',
      phone: '',
      projectType: 'Residential',
      tileInterest: initialTileInterest,
      message: '',
      honeypot: '',
    });
    setFieldErrors(INITIAL_ERRORS);
  };

  if (status === 'success') {
    return (
      <div className="inquiry-form">
        <div className="inquiry-form__success">
          <span className="inquiry-form__success-icon" aria-hidden="true">✓</span>
          <h3 className="inquiry-form__success-title">Message Sent!</h3>
          <p className="inquiry-form__success-text">
            Thank you for reaching out. A member of our team will be in touch
            within one business day.
          </p>
          <Button variant="secondary" size="sm" onClick={handleSendAnother}>
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="inquiry-form">
      <h3 className="inquiry-form__title">Send Us a Message</h3>

      <form onSubmit={handleSubmit} noValidate>
        <div className="inquiry-form__grid">
          {/* Honeypot */}
          <div className="inquiry-form__honeypot" aria-hidden="true">
            <input
              type="text"
              name="honeypot"
              value={fields.honeypot}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Full Name */}
          <div className="inquiry-form__field">
            <label className="inquiry-form__label" htmlFor="fullName">
              Full Name<span className="inquiry-form__required">*</span>
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              className="inquiry-form__input"
              value={fields.fullName}
              onChange={handleChange}
              disabled={isDisabled}
              autoComplete="name"
              placeholder="Jane Smith"
            />
            {fieldErrors.fullName && (
              <span className="inquiry-form__error-text">{fieldErrors.fullName}</span>
            )}
          </div>

          {/* Email */}
          <div className="inquiry-form__field">
            <label className="inquiry-form__label" htmlFor="email">
              Email<span className="inquiry-form__required">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              className="inquiry-form__input"
              value={fields.email}
              onChange={handleChange}
              disabled={isDisabled}
              autoComplete="email"
              placeholder="jane@example.com"
            />
            {fieldErrors.email && (
              <span className="inquiry-form__error-text">{fieldErrors.email}</span>
            )}
          </div>

          {/* Phone */}
          <div className="inquiry-form__field">
            <label className="inquiry-form__label" htmlFor="phone">
              Phone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              className="inquiry-form__input"
              value={fields.phone}
              onChange={handleChange}
              disabled={isDisabled}
              autoComplete="tel"
              placeholder="(713) 555-0000"
            />
          </div>

          {/* Project Type */}
          <div className="inquiry-form__field">
            <label className="inquiry-form__label" htmlFor="projectType">
              Project Type
            </label>
            <select
              id="projectType"
              name="projectType"
              className="inquiry-form__select"
              value={fields.projectType}
              onChange={handleChange}
              disabled={isDisabled}
            >
              {PROJECT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Tile of Interest */}
          <div className="inquiry-form__field inquiry-form__field--full">
            <label className="inquiry-form__label" htmlFor="tileInterest">
              Tile of Interest
            </label>
            <input
              id="tileInterest"
              name="tileInterest"
              type="text"
              className="inquiry-form__input"
              value={fields.tileInterest}
              onChange={handleChange}
              disabled={isDisabled}
              placeholder="e.g. Calacatta Gold, Bianco Lux..."
            />
          </div>

          {/* Message */}
          <div className="inquiry-form__field inquiry-form__field--full">
            <label className="inquiry-form__label" htmlFor="message">
              Message<span className="inquiry-form__required">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              className="inquiry-form__textarea"
              value={fields.message}
              onChange={handleChange}
              disabled={isDisabled}
              placeholder="Tell us about your project, square footage, timeline, or any other questions..."
            />
            {fieldErrors.message && (
              <span className="inquiry-form__error-text">{fieldErrors.message}</span>
            )}
          </div>

          {/* Error banner */}
          {status === 'error' && (
            <div className="inquiry-form__error-banner">
              {errorMessage || 'Something went wrong. Please try again.'}
            </div>
          )}

          {/* Submit */}
          <div className="inquiry-form__submit-row">
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={isDisabled}
            >
              {isDisabled ? 'Sending…' : 'Send Message'}
            </Button>
            {isDisabled && <LoadingSpinner size="sm" />}
          </div>
        </div>
      </form>
    </div>
  );
}
